import { useState } from 'react';

interface Props {
  latitude: string;
  longitude: string;
  compact?: boolean;
}

/** Botão "Rotas" que abre um seletor Google Maps / Waze (feedback Saulo
 * 2026-07-08) em vez de sair direto pro Google Maps. Ícones são os PNGs
 * oficiais fornecidos por Saulo (`design/logos/` → `public/logo/`), não
 * desenhos aproximados. Escondido quando a estação não tem coordenadas —
 * ver `HomeStationSheetExpanded.tsx`. */
export function RoutesButton({ latitude, longitude, compact = false }: Props) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`inline-flex flex-shrink-0 items-center justify-center gap-2 rounded-full border-[1.5px] border-bow-royal/18 bg-white text-sm font-semibold text-bow-royal ${
          compact ? 'h-10 px-3.5' : 'h-11 self-start px-4'
        }`}
      >
        <RouteIcon />
        Rotas
      </button>
      {open && (
        <RouteAppSheet
          latitude={latitude}
          longitude={longitude}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function RouteAppSheet({
  latitude,
  longitude,
  onClose
}: {
  latitude: string;
  longitude: string;
  onClose: () => void;
}) {
  const googleUrl = `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`;
  const wazeUrl = `https://waze.com/ul?ll=${latitude},${longitude}&navigate=yes`;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center bg-black/60"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="flex w-full max-w-md flex-col gap-2.5 rounded-t-3xl bg-white p-5 text-bow-royal"
        style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}
      >
        <div className="mx-auto mb-1 h-[4.5px] w-10 flex-shrink-0 rounded-full bg-bow-royal/15" />
        <span className="px-1 text-sm font-semibold text-bow-royal/70">Traçar rota com</span>
        <div className="flex gap-3">
          <RouteAppOption
            href={googleUrl}
            label="Google Maps"
            onClick={onClose}
            icon={<img src="/logo/google-maps.png" alt="" width={44} height={44} className="rounded-xl" />}
          />
          <RouteAppOption
            href={wazeUrl}
            label="Waze"
            onClick={onClose}
            icon={<img src="/logo/waze.png" alt="" width={44} height={44} className="rounded-xl" />}
          />
        </div>
        <button
          type="button"
          onClick={onClose}
          className="mt-1 inline-flex h-11 items-center justify-center rounded-full text-sm font-semibold text-bow-royal/60"
        >
          Cancelar
        </button>
      </div>
    </div>
  );
}

function RouteAppOption({
  href,
  label,
  icon,
  onClick
}: {
  href: string;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      onClick={onClick}
      className="flex flex-1 flex-col items-center gap-2 rounded-2xl border border-bow-royal/8 bg-bow-cream-2/40 p-4"
    >
      {icon}
      <span className="text-[13px] font-semibold text-bow-royal">{label}</span>
    </a>
  );
}

function RouteIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M9.5 20 4 12l5.5-8h5L20 12l-5.5 8z" />
      <path d="M11 14.5v-3h3.5" />
    </svg>
  );
}
